import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Owns the page's scroll position across navigation.
 *
 * Two jobs:
 *
 * 1. A new route starts at the top — explicitly NOT on search-param updates
 *    and NOT on REPLACE-style navigation.
 *
 *    The original effect deps `[pathname, navType]` fired the scroll whenever
 *    EITHER value changed. setSearchParams with replace:true (used by every
 *    filter on the catalog + group + family pages) flips navType from "PUSH"
 *    to "REPLACE" but doesn't change pathname — and that flip alone triggered
 *    the effect, so every checkbox click inside a filter sidebar yanked the
 *    page back to the top. Hence the gate on the *previous pathname* differing
 *    from the current one; navType is consulted as a guard but doesn't drive
 *    re-runs on its own.
 *
 * 2. Going back returns the reader where they were.
 *
 *    This used to be left to the browser, and the browser cannot get it right
 *    here. On a same-document history traversal Chrome applies its restore
 *    while the document being left is still the layout in effect, so the
 *    offset is clamped to THAT document's height. Leaving the home page from
 *    the events section (~3.8 k px down a ~6.6 k px page) for an event detail
 *    page (~2.2 k px) clamped the return to 1524 px — 2244 minus the viewport
 *    — dropping the reader into the middle of the product catalogue, three
 *    sections above where they had been. Pressing Back within the same tick as
 *    the click happened to land correctly, which is why it looked intermittent:
 *    any real pause on the event page reproduced it every time.
 *
 *    So we record the offset per history entry and re-apply it ourselves after
 *    React has committed the destination. `history.scrollRestoration` is left
 *    at "auto" deliberately: the browser's restore for a full document reload
 *    is accurate (the page reloads at full height) and switching to "manual"
 *    would take that over for no gain.
 */

// useLayoutEffect warns when React renders on the server, and entry-server.tsx
// renders this component to keep the tree shape identical to App.tsx. Neither
// effect runs there; the alias just silences the warning.
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

// Scroll offset per history entry, keyed by react-router's location.key.
// Module scope so it outlives route changes. A full document load starts a
// fresh map, which is correct — the browser handles those itself.
const offsets = new Map<string, number>();

// How long a restore may keep re-applying itself while the destination is
// still moving. Generous enough for images and lazy sections to resolve on a
// slow connection, short enough that a stale loop is never left fighting
// anything the reader does next.
const SETTLE_MS = 1500;

// Consecutive frames at an unchanged document height that count as "layout has
// stopped moving". Stopping the moment the page is merely tall enough is not
// sufficient — content above the target settles afterwards and drags the
// reader off by a couple of hundred pixels.
const STABLE_FRAMES = 5;

// How long scrolling must be quiet before the offset is committed to the
// entry on screen. Long enough that a page unwinding out of a navigation
// never settles, short enough to catch a reader who scrolls and immediately
// taps.
const SETTLE_QUIET_MS = 120;

// A reader who starts scrolling mid-restore wins. "scroll" is deliberately not
// in this list — our own scrollTo would abort the loop on its first frame.
const ABORT_EVENTS = ["wheel", "touchstart", "keydown"] as const;

/**
 * Put the page back at `target` and hold it there until layout stops moving.
 * Returns a cancel function.
 */
function restoreOffset(target: number): () => void {
  let raf = 0;
  let stopped = false;
  let lastHeight = -1;
  let stableFrames = 0;
  const deadline = performance.now() + SETTLE_MS;

  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    cancelAnimationFrame(raf);
    for (const evt of ABORT_EVENTS) window.removeEventListener(evt, stop);
  };

  const step = (): void => {
    if (stopped) return;
    const height = document.documentElement.scrollHeight;
    const max = Math.max(0, height - window.innerHeight);
    const desired = Math.min(target, max);

    // Read BEFORE correcting: this is whether anything moved the page since
    // the last frame. Scroll anchoring reacting to images landing, and the
    // browser's own late restore, both nudge the offset by tens of pixels
    // after the document has stopped growing — watching the height alone let
    // the loop finish while those were still to come.
    const drifted = Math.abs(window.scrollY - desired) > 1;

    // `html { scroll-behavior: smooth }` is global — an animated restore would
    // still be travelling when the reader starts reading.
    window.scrollTo({ top: desired, left: 0, behavior: "instant" });

    if (drifted || height !== lastHeight) stableFrames = 0;
    else stableFrames++;
    lastHeight = height;

    // Done once the page reaches the offset and nothing has moved it for a
    // few frames running. A page that can never reach it (content genuinely
    // removed) falls out on the deadline instead of spinning.
    const settled = stableFrames >= STABLE_FRAMES && max >= target;
    if (!settled && performance.now() < deadline) {
      raf = requestAnimationFrame(step);
    } else {
      stop();
    }
  };

  for (const evt of ABORT_EVENTS) {
    window.addEventListener(evt, stop, { passive: true, once: true });
  }
  step();
  return stop;
}

export const ScrollToTop = () => {
  const { pathname, key } = useLocation();
  const navType = useNavigationType();
  const prevPathname = useRef(pathname);
  const activeKey = useRef(key);

  // One listener for the document's life, writing to whichever entry is on
  // screen.
  //
  // Committing on a settle timer rather than on every scroll event is load
  // bearing. `v7_startTransition` defers React's commit, so the layout effect
  // below — the thing that repoints activeKey at the new route — runs AFTER
  // the browser has already dispatched scroll events for the page unwinding
  // out of the old one. Writing those straight through recorded the outgoing
  // entry's offset as a half-finished 5173 instead of the 5254 the reader was
  // actually at, and the restore then faithfully reproduced the wrong number.
  // Frames from an unwinding page never come to rest, so they never commit;
  // the reader's resting position does.
  useEffect(() => {
    let settle = 0;
    const commit = (): void => {
      offsets.set(activeKey.current, window.scrollY);
    };
    const record = (): void => {
      window.clearTimeout(settle);
      settle = window.setTimeout(commit, SETTLE_QUIET_MS);
    };
    // The gesture that starts a navigation is the last moment the page is
    // certainly still at the reader's position, so take it as authoritative —
    // otherwise a tap within the settle window would leave a stale offset.
    window.addEventListener("scroll", record, { passive: true });
    window.addEventListener("pointerdown", commit, { passive: true });
    window.addEventListener("keydown", commit, { passive: true });
    return () => {
      window.clearTimeout(settle);
      window.removeEventListener("scroll", record);
      window.removeEventListener("pointerdown", commit);
      window.removeEventListener("keydown", commit);
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    const pathChanged = prevPathname.current !== pathname;
    prevPathname.current = pathname;
    activeKey.current = key;

    if (navType === "POP") {
      const saved = offsets.get(key);
      // Nothing recorded for this entry — it predates this document, or the
      // reader never scrolled it. Either way the browser's own restore is as
      // good as anything we could invent.
      return saved === undefined ? undefined : restoreOffset(saved);
    }

    if (pathChanged) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [pathname, navType, key]);

  return null;
};
